import { gql, useQuery } from '@apollo/client';
// import type { QueryResult } from '@livecontrol/scheduler/store';
import { useEffect, useState } from 'react';
import type { Installer } from '../../../model';
import * as Dbx from './dbx';

interface TData {
  getInstallers?: {
    total: number;
    page: number;
    software_versions: Dbx.Record[];
  };
}

interface TVariables {
  input: unknown;
}

interface Pagination {
  page?: number;
  limit?: number;
  sort_field?: string;
  order?: string;
}

type Filters = Partial<{
  url: string;
  notes: string;
  name: string;
  id: number;
  expirationTime: number;
  app: string;
}>;

type ExtraFilters = Partial<{
  wildcard: string;
}>;

type Props = Partial<{
  pagination: Pagination;
  filters: Filters;
  extraFilters: ExtraFilters;
}>;

const QUERY = gql`
query GetInstallers($input: GetInstallersInput) {
  getInstallers(input: $input) {
    total
    page
    software_versions {
      url
      notes
      name
      id
      expirationTime
      date_updated
      date_created
      app
    }
  }
}
`;

const normalizeSortField = (sortField?: string): string | undefined => {
  switch (sortField) {
    case 'app':
    case 'date_created':
      return sortField;
    default:
      return undefined;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useInstallers = ({ pagination = { limit: 5, page: 1 }, filters, extraFilters }: Props = {}) => {
  const [hasMore, setHastMore] = useState(false);
  const [page, setPage] = useState(pagination.page ?? 1);
  const [totalPages, setTotalPages] = useState(1);
  const [installers, setInstallers] = useState<Installer[]>();

  const { data, loading, error, refetch } = useQuery<TData, TVariables>(QUERY, {
    variables: {
      input: {
        pagination: {
          limit: pagination.limit ?? 5,
          page: pagination.page ?? 1,
          sort_field: normalizeSortField(pagination.sort_field),
          order: pagination.order
        },
        filters,
        extra_filter: extraFilters
      }
    }
  });


  useEffect(() => {
    let installers_;

    if (data?.getInstallers) {
      const { getInstallers: { page: currentPage, total }} = data;

      installers_ = data.getInstallers.software_versions
      .map(Dbx.normalize)
      .filter((v): v is Installer => !!v);

      setHastMore((((currentPage - 1) * (pagination.limit ?? 5)) + installers_.length) < total);
      setPage(currentPage);
      setTotalPages(Math.ceil(total / (pagination.limit ?? 5)));

    }

    setInstallers(installers_);
  }, [data, pagination.limit]);

  return {
    loading,
    installers,
    error,
    page,
    totalPages,
    hasMore,
    refetch
  };
};
